import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Container } from '../layout/Container'
import { Button } from '../ui/Button'
import { useAuth } from '../../hooks/useAuth'
import { Brain, FolderOpen, Search, Upload, Menu, X, LogOut, ChevronDown, LayoutDashboard } from 'lucide-react'
import { cn } from '../../lib/utils'

const navItems = [
  { label: 'Ingesta', href: '/app/ingest', icon: Upload },
  { label: 'Bóveda', href: '/app/vault', icon: FolderOpen },
  { label: 'Búsqueda', href: '/app/search', icon: Search },
]

export function KnowledgeLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, tenants, selectTenant, logout } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showTenantMenu, setShowTenantMenu] = useState(false)

  const currentTenant = tenants.find(
    (tenant) => tenant.id === localStorage.getItem('selectedTenantId')
  ) ?? tenants[0]

  const handleTenantSwitch = (tenantId: string) => {
    selectTenant(tenantId)
    setShowTenantMenu(false)
    window.location.reload()
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <div className="flex min-h-screen bg-bg">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-surface lg:flex">
        <div className="flex h-16 items-center border-b border-border px-6">
          <Link to="/" className="flex items-center gap-2 text-xl font-semibold text-gradient">
            <Brain className="h-6 w-6 text-accent-light" />
            <span>EmpleaHoy IA</span>
          </Link>
        </div>

        <div className="border-b border-border p-4">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTenantMenu(!showTenantMenu)}
              className="flex w-full items-center justify-between rounded-lg border border-border bg-bg px-3 py-2 text-sm transition-colors hover:border-accent/30"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-accent text-xs font-bold text-white">
                  {currentTenant?.name.charAt(0).toUpperCase()}
                </div>
                <span className="font-medium text-text-primary">{currentTenant?.name}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-text-muted" />
            </button>

            {showTenantMenu && tenants.length > 1 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-border bg-surface-elevated py-1 shadow-xl">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    type="button"
                    onClick={() => handleTenantSwitch(tenant.id)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-surface',
                      tenant.id === currentTenant?.id ? 'text-accent-light' : 'text-text-secondary'
                    )}
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 text-xs font-bold text-accent-light">
                      {tenant.name.charAt(0).toUpperCase()}
                    </div>
                    {tenant.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 space-y-1 p-4">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent/10 text-accent-light'
                    : 'text-text-secondary hover:bg-surface hover:text-text-primary'
                )}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="border-t border-border p-4">
          <div className="mb-3 text-xs text-text-muted">{user?.email}</div>
          <Link
            to="/admin"
            className="mb-2 flex w-full items-center justify-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:border-accent hover:text-text-primary"
          >
            <LayoutDashboard className="h-4 w-4" />
            Administración
          </Link>
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            {t('admin.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="fixed left-0 right-0 top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-surface px-4 lg:hidden">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold text-gradient">
          <Brain className="h-5 w-5 text-accent-light" />
          <span>EmpleaHoy IA</span>
        </Link>
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(true)}
          className="rounded-full p-2 text-text-secondary hover:bg-surface-elevated"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-bg/95 backdrop-blur-xl lg:hidden">
          <Container className="flex h-full flex-col py-4">
            <div className="flex items-center justify-between">
              <span className="text-xl font-semibold text-gradient">EmpleaHoy IA</span>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-full p-2 text-text-secondary hover:bg-surface-elevated"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <nav className="mt-8 flex flex-col gap-2">
              {navItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-3 text-lg font-medium text-text-secondary hover:bg-surface hover:text-text-primary"
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                )
              })}
            </nav>

            <div className="mt-auto">
              <div className="mb-3 text-sm text-text-muted">{user?.email}</div>
              <Link
                to="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mb-2 flex w-full items-center justify-center gap-2 rounded-full border border-border-subtle px-4 py-3 text-base font-medium text-text-secondary transition-colors hover:border-accent hover:text-text-primary"
              >
                <LayoutDashboard className="h-5 w-5" />
                Administración
              </Link>
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('admin.logout')}
              </Button>
            </div>
          </Container>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:pl-64">
        <div className="min-h-screen pt-16 lg:pt-0">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
